/**
 * Compartir por link (SDD §7.5): alcance, ocultar nombres y vencimiento. El
 * link completo se muestra una sola vez (en la base solo queda su hash).
 */
import { useEffect, useState } from 'react'
import type { RecordingDetail, Share } from '@eco/shared'
import { useApp, useToast, errorText } from './AppContext'
import { Modal } from './ui'
import { formatDate } from '../lib/format'

export function ShareDialog({
  open,
  onClose,
  detail,
  onChanged
}: {
  open: boolean
  onClose: () => void
  detail: RecordingDetail
  onChanged: () => Promise<void>
}) {
  const { api } = useApp()
  const toast = useToast()
  const [alcance, setAlcance] = useState<Share['alcance']>('notas')
  const [ocultar, setOcultar] = useState(false)
  const [dias, setDias] = useState<'7' | '30' | '90' | 'nunca'>('30')
  const [url, setUrl] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (open) setUrl(null)
  }, [open])

  const activos = detail.shares.filter(
    (s) => !s.revocadoEn && (!s.expiraEn || new Date(s.expiraEn) > new Date())
  )

  async function create() {
    setBusy(true)
    try {
      const r = await api.createShare(detail.recording.id, {
        alcance,
        ocultarNombres: ocultar,
        expiraEnDias: dias === 'nunca' ? null : (Number(dias) as 7 | 30 | 90)
      })
      setUrl(r.url)
      await onChanged()
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  async function revoke(id: string) {
    try {
      await api.revokeShare(id)
      toast('Link desactivado')
      await onChanged()
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Compartir por link">
      {url ? (
        <div className="stack">
          <div className="notice good">
            Copia el link ahora: por seguridad no lo volveremos a mostrar. Quien lo tenga podrá ver
            {alcance === 'notas' ? ' las notas' : ' las notas y la transcripción'}, sin iniciar
            sesión.
          </div>
          <div className="row">
            <input
              className="grow"
              readOnly
              value={url}
              onFocus={(e) => e.target.select()}
              aria-label="Link para compartir"
            />
            <button
              className="btn primary"
              onClick={() =>
                void navigator.clipboard
                  .writeText(url)
                  .then(() => toast('Link copiado'))
                  .catch(() =>
                    toast('No se pudo copiar. Revisa el permiso del portapapeles.', 'error')
                  )
              }
            >
              Copiar
            </button>
          </div>
        </div>
      ) : (
        <div className="stack">
          <label className="field">
            <span>Qué se comparte</span>
            <select
              value={alcance}
              onChange={(e) => setAlcance(e.target.value as Share['alcance'])}
            >
              <option value="notas">Solo las notas</option>
              <option value="notas_transcripcion">Notas y transcripción</option>
            </select>
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={ocultar}
              onChange={(e) => setOcultar(e.target.checked)}
            />
            <span>Ocultar los nombres (se muestran como «Participante N»)</span>
          </label>
          <label className="field">
            <span>Vence en</span>
            <select value={dias} onChange={(e) => setDias(e.target.value as typeof dias)}>
              <option value="7">7 días</option>
              <option value="30">30 días</option>
              <option value="90">90 días</option>
              <option value="nunca">No vence</option>
            </select>
          </label>
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button className="btn primary" onClick={() => void create()} disabled={busy}>
              Crear link
            </button>
          </div>
        </div>
      )}
      {activos.length > 0 && (
        <div className="stack" style={{ gap: 6 }}>
          <h3>Links activos</h3>
          {activos.map((s) => (
            <div key={s.id} className="row" style={{ fontSize: 13 }}>
              <span className="grow muted">
                {s.alcance === 'notas' ? 'Notas' : 'Notas y transcripción'}
                {s.ocultarNombres ? ' · sin nombres' : ''} ·{' '}
                {s.expiraEn ? `vence el ${formatDate(s.expiraEn)}` : 'no vence'}
              </span>
              <button className="btn sm ghost danger" onClick={() => void revoke(s.id)}>
                Desactivar
              </button>
            </div>
          ))}
        </div>
      )}
    </Modal>
  )
}
